import React, { useEffect, useState } from 'react';
import Style from './sidebarStyle.module.scss';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from 'oidc-react';
import { ACTIVITY_LOG_ALLOWED_ROLES, PERMISSIONS, Role } from '../../common/Utils/permissions';
import { isDevelopmentEnvironment } from '../../common/Utils/environment';
import { Package, Settings, FileText, History } from 'lucide-react';
import MarketplaceIcon from './MarketplaceIcon';
import { useTenantScope } from '../../hooks/useTenantScope';

type MenuItem = {
  name: string;
  key: string;
  link: string;
  type: 'img' | 'icon';
  image?: any;
  icon?: React.ComponentType<any>;
  hidden?: boolean;
};

const ADMIN_SECTION_KEYS = ['TenantManagement', 'Settings', 'AccessControl', 'Support'];
const SHOW_ADMIN_SECTION_DIVIDER = false;

const SidebarItems = () => {
  const location = useLocation(); // To get the current URL
  const [activeMenuKey, setActiveMenuKey] = useState('Dashboard');
  const showMarketplace = isDevelopmentEnvironment();

  const auth = useAuth();
  const role = auth?.userData?.profile?.role ?? 'tenantadmin';
  const perms = PERMISSIONS[role as Role] || {};
  const { isMenuAllowed: isMenuAllowedInScope } = useTenantScope();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]); // store menu items in state so they update dynamically

  useEffect(() => {
    // rebuild menu items whenever role changes
    const items: MenuItem[] = [
      { name: 'Dashboard', key: 'Dashboard', image: require('../../icons/dashboard.png'), link: '/', type: 'img' },
      { name: 'Patch Center', key: 'PatchCenter', image: require('../../icons/patch_centre.png'), link: '/patchCenter', type: 'img' },
      {
        name: 'Vulnerability Center',
        key: 'VulnerabilityCenter',
        image: require('../../icons/vulnerability.png'),
        link: '/vulnerabilityCenter',
        type: 'img',
      },
      {
        name: 'Software Center',
        key: 'SoftwareInventory',
        image: require('../../icons/software_assets.png'),
        link: '/SoftwareAssets',
        type: 'img',
      },
      {
        name: 'Device Center',
        key: 'DeviceManagement',
        image: require('../../icons/device_management.png'),
        link: '/deviceManagement',
        type: 'img',
      },
      { name: 'Group Policies', key: 'GroupPolicies', image: require('../../icons/policies.png'), link: '/Group', type: 'img' },
      {
        name: 'Script Center',
        key: 'ScriptCenter',
        image: require('../../icons/vulnerability.png'),
        link: '/ScriptAutomation',
        type: 'img',
      },
      { name: 'Report', key: 'Report', icon: FileText, link: '/report', type: 'icon' },
      { name: 'Activity Log', key: 'ActivityLog', icon: History, link: '/activity-log', type: 'icon' },
      { name: 'Package Management', key: 'PackageManager', icon: Package, link: '/packagemanager', type: 'icon' },
      {
        name: 'Tenant Management',
        key: 'TenantManagement',
        image: require('../../icons/tenant_management.png'),
        link: '/customers',
        type: 'img',
      },
      { name: 'Settings', key: 'Settings', icon: Settings, link: '/settings', type: 'icon' },
      { name: 'Access Control', key: 'AccessControl', image: require('../../icons/admin.png'), link: '/Accounts', type: 'img' },
      { name: 'Support', key: 'Support', image: require('../../icons/guide.png'), link: '/GuideAndSupport', type: 'img' },
    ];

    if (showMarketplace) {
      items.push({ name: 'Marketplace', key: 'Marketplace', icon: MarketplaceIcon, link: '/marketplace', type: 'icon' });
    }

    setMenuItems(items);
  }, [showMarketplace]);

  const handleMenuClick = (menuKey: string) => {
    // Check if the current URL contains 'action'
    if (
      window.location.href.includes('/deviceSelect') ||
      window.location.href.includes('/Action') ||
      window.location.href.includes('/AvailablePatch') ||
      window.location.href.includes('/PredictiveAnalysis')
    ) {
      setActiveMenuKey('PatchCenter'); // Keep Patch Center active if action-related route is open
    } else {
      setActiveMenuKey(menuKey);
    }
  };

  React.useEffect(() => {
    // Automatically set active menu key based on the current URL
    const currentMenuItem = menuItems.find((item) => item.link === location.pathname);

    // Check if the URL contains 'action'
    if (
      window.location.href.includes('/deviceSelect') ||
      window.location.href.includes('/Action') ||
      window.location.href.includes('/AvailablePatch') ||
      window.location.href.includes('/PredictiveAnalysis') ||
      window.location.href.includes('/Preview')
    ) {
      setActiveMenuKey('PatchCenter');
    } else if (
      window.location.href.includes('/CreatePolicy') ||
      window.location.href.includes('/CreateGroup') ||
      window.location.href.includes('/ThirdParty') ||
      window.location.href.includes('/OS-Update') ||
      window.location.href.includes('/groupPolicy') ||
      window.location.href.includes('/Group')
    ) {
      setActiveMenuKey('GroupPolicies');
    } else if (window.location.href.includes('/CreateCustomer')) {
      setActiveMenuKey('TenantManagement');
    } else if (window.location.href.includes('patchCenter/ActivityPatch')) {
      setActiveMenuKey('PatchCenter');
    } else if (window.location.href.includes('/patchcenter/RemediationReport')) {
      setActiveMenuKey('PatchCenter');
    } else if (window.location.href.includes('/DeviceInsights')) {
      setActiveMenuKey('PatchCenter');
    } else if (window.location.href.includes('patchCenter/AvailabilityPatch')) {
      setActiveMenuKey('PatchCenter');
    } else if (window.location.href.includes('/PatchDeployment')) {
      setActiveMenuKey('SoftwareInventory');
    } else if (window.location.href.includes('/SoftwareDeployment')) {
      setActiveMenuKey('SoftwareInventory');
    } else if (window.location.href.includes('/SoftwareAssets')) {
      setActiveMenuKey('SoftwareInventory');
    } else if (window.location.href.includes('deviceManagement/info/')) {
      setActiveMenuKey('DeviceManagement');
    } else if (window.location.href.includes('ScriptAutomation/CreateNewScript')) {
      setActiveMenuKey('ScriptCenter');
    } else if (window.location.href.includes('ScriptAutomation/EditScript')) {
      setActiveMenuKey('ScriptCenter');
    } else if (showMarketplace && window.location.href.includes('/marketplace')) {
      setActiveMenuKey('Marketplace');
    } else if (window.location.href.includes('/packagemanager')) {
      setActiveMenuKey('PackageManager');
    } else if (window.location.href.includes('Accounts/CreateAccount')) {
      setActiveMenuKey('AccessControl');
    } else if (window.location.href.includes('/settings')) {
      setActiveMenuKey('Settings');
    } else if (window.location.href.includes('/report')) {
      setActiveMenuKey('Report');
    } else if (window.location.href.includes('GuideAndSupport')) {
      setActiveMenuKey('Support');
    } else if (currentMenuItem) {
      setActiveMenuKey(currentMenuItem.key);
    }
  }, [location.pathname, menuItems, showMarketplace]);

  const menuItemsWithIndex = menuItems.map((item, index) => ({ item, index }));
  const topMenuItems = menuItemsWithIndex.filter(({ item }) => item.key !== 'Marketplace');
  const marketplaceMenuItem = menuItemsWithIndex.find(({ item }) => item.key === 'Marketplace');
  // Activity Log is allow-listed by role (same list as its route), so roles missing from PERMISSIONS don't get it.
  // In their own tenant, Globaladmin and reseller only get the admin menus (tenantScope.ts).
  const isAllowedMenu = (key: string) => (key !== 'ActivityLog' || ACTIVITY_LOG_ALLOWED_ROLES.includes(role)) && isMenuAllowedInScope(key);
  const visibleTopMenuItems = topMenuItems.filter(
    ({ item }) => !perms.hiddenMenus?.includes(item.key) && !item.hidden && isAllowedMenu(item.key),
  );
  const firstAdminItemIndex = visibleTopMenuItems.findIndex(({ item }) => ADMIN_SECTION_KEYS.includes(item.key));

  return (
    <div className="text-white h-full min-h-0 flex flex-col">
      <nav className={`flex-1 min-h-0 mt-4 ${Style.topMenuContainer}`}>
        <ul className="space-y-2">
          {visibleTopMenuItems.map(({ item }, index) => (
            <React.Fragment key={item.key}>
              {/* Comment out the next line if you do not want the admin-section divider */}
              {SHOW_ADMIN_SECTION_DIVIDER && index === firstAdminItemIndex && <li className={Style.adminSectionDivider} />}
              <li>
                <Link
                  to={item.link}
                  className={`flex items-center py-[15px] pl-[15px] rounded  ${Style.hoverActive} ${item.key === activeMenuKey ? Style.active : ''}`}
                  onClick={() => handleMenuClick(item.key)}
                >
                  {item.type === 'icon' && item.icon ? (
                    <item.icon className={Style.icon_size} style={{ width: '20px', height: '20px', marginRight: '12px' }} />
                  ) : (
                    item.image && <img src={item.image} className={Style.icon_size} alt={`${item.name} icon`} />
                  )}
                  <span className={Style.menulabel}>{item.name}</span>
                </Link>
              </li>
            </React.Fragment>
          ))}
        </ul>
      </nav>

      {marketplaceMenuItem &&
        !perms.hiddenMenus?.includes(marketplaceMenuItem.item.key) &&
        !marketplaceMenuItem.item.hidden &&
        isAllowedMenu(marketplaceMenuItem.item.key) && (
          <div className={Style.marketplaceContainer}>
            <Link
              to={marketplaceMenuItem.item.link}
              className={`flex items-center py-[15px] pl-[15px] ${Style.marketplaceLink} ${marketplaceMenuItem.item.key === activeMenuKey ? Style.marketplaceLinkActive : ''}`}
              onClick={() => handleMenuClick(marketplaceMenuItem.item.key)}
            >
              {marketplaceMenuItem.item.type === 'icon' && marketplaceMenuItem.item.icon ? (
                <marketplaceMenuItem.item.icon className={Style.icon_size} style={{ width: '20px', height: '20px', marginRight: '12px' }} />
              ) : (
                marketplaceMenuItem.item.image && (
                  <img src={marketplaceMenuItem.item.image} className={Style.icon_size} alt={`${marketplaceMenuItem.item.name} icon`} />
                )
              )}
              <span className={Style.marketplaceLabel}>{marketplaceMenuItem.item.name}</span>
            </Link>
          </div>
        )}
    </div>
  );
};

export default SidebarItems;
